import {
  Avatar,
  styled,
  NoSsr,
  useTheme,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import type { Property } from "csstype";
import { SEE_MORE } from "../../../theme/defaultConstants";
import {
  defaultFocusOutline,
  handleKeyEvent,
} from "../../helpers/accessibility";
import { mergeArraysUnique } from "../../helpers/array";
import { ThemeChip } from "./ThemeComponents/ThemeChip";
import { Option as FilterOption, OptionValue } from "./Filter";
import { SeoAnchor } from "./anchor/SeoAnchor";
import { SeeMoreButton } from "./SeeMoreButton";
import { SSR } from "../../helpers/environment";
import { useReRenderOnResize } from "../../helpers/reRenderOnResize";

type Height = Property.Height<string | number>;

const animationTime = 250;

interface Props {
  options: FilterOption[];
  onChange: (checked: OptionValue[]) => void;
  initialSelectedOptionValues?: OptionValue[];
  maxRowsBeforeShowMore?: number;
  seoHrefPrefix: string;
  title: string;
  variant: "primary" | "secondary";
}
const margin = 8;
const defaultHeight = 200;

const OptionContainer = styled("li")(() => ({
  display: "inline-block",
}));

const ButtonContainer = styled("div")(() => ({
  textAlign: "center",
  marginTop: margin,
}));

const ClickableChip = styled(ThemeChip)(() => ({
  cursor: "pointer",
  lineHeight: 1,
  "& .MuiChip-label": {
    overflow: "visible",
  },
}));

const StyledChipAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: `${theme.palette.text.chipDefault} !important`,
  lineHeight: 0,
  width: 24,
  height: 24,
  "& span": {
    fontSize: 16,
  },
}));

const OptionListContainer = styled("ul")<{
  currentHeight: Height;
}>(({ currentHeight }) => ({
  height: currentHeight,
  overflow: "hidden",
  gap: margin,
  display: "flex",
  flexWrap: "wrap",
  textAlign: "inherit",
  transition: `${animationTime}ms ease-in-out`,
}));

const StyledCheckbox = styled(Checkbox)(() => ({
  border: 0,
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  margin: 0,
  position: "relative",
  "&:focus-within": {
    "& .MuiChip-root": {
      outline: defaultFocusOutline(theme),
      outlineOffset: -3,
    },
  },
}));

const ChipIconLabel = styled("div")(({ theme }) => ({
  gap: theme.spacing(1),
  display: "flex",
  alignItems: "center",
}));

interface OptionProps extends Pick<Props, "variant"> {
  option: FilterOption;
  checked: boolean;
  onChange: () => void;
  seoHrefPrefix: string;
}
const Option = ({
  option: { detailsLabel, label, value },
  checked,
  onChange,
  variant,
  seoHrefPrefix,
}: OptionProps) => (
  <StyledFormControlLabel
    aria-label={`${label}${detailsLabel ? ` ${detailsLabel}` : ""}`}
    onClick={onChange}
    control={
      <>
        <StyledCheckbox
          checked={checked}
          onKeyDown={handleKeyEvent({
            onEnter: onChange,
          })}
        />
      </>
    }
    label={
      <SeoAnchor tabIndex={-1} href={`${seoHrefPrefix}${value}`}>
        <ClickableChip
          variant={checked ? "filled" : "outlined"}
          color="primary"
          label={
            variant === "secondary" ? (
              <ChipIconLabel>
                {label + (detailsLabel ? ` (${detailsLabel})` : "")}
              </ChipIconLabel>
            ) : (
              label
            )
          }
          avatar={
            variant !== "secondary" && detailsLabel ? (
              <StyledChipAvatar>{detailsLabel}</StyledChipAvatar>
            ) : undefined
          }
          tabIndex={-1}
        />{" "}
      </SeoAnchor>
    }
  />
);

// eslint-disable-next-line react/no-multi-comp
const OptionList: FunctionComponent<Props> = ({
  options,
  onChange,
  seoHrefPrefix,
  initialSelectedOptionValues = [],
  maxRowsBeforeShowMore = 3,
  title,
  variant,
}: Props) => {
  const theme = useTheme();
  const [optionHeight, setOptionHeight] = useState(defaultHeight);
  const [containerHeight, setContainerHeight] = useState<Height | undefined>();
  const [open, setOpen] = useState(false);
  const [doneRendering, setDoneRendering] = useState(false);
  const [hideButton, setHideButton] = useState(false);
  useReRenderOnResize();
  const rowHeight = optionHeight + margin;

  const heightByMaxRows = rowHeight * maxRowsBeforeShowMore;

  const allOptionsAreShowed =
    typeof containerHeight === "number" && heightByMaxRows > containerHeight;

  const collapsedHeight = allOptionsAreShowed
    ? containerHeight
    : heightByMaxRows;

  const showSeeMore = !hideButton && !allOptionsAreShowed;

  const currentContainerHeight = open
    ? containerHeight || "auto"
    : collapsedHeight - (showSeeMore ? margin : 0) || "auto";

  useEffect(() => {
    setDoneRendering(true);
  }, []);

  const handleChange = useCallback(
    (isChecked: boolean, value: string) => {
      const newValues = !isChecked
        ? mergeArraysUnique(initialSelectedOptionValues, [value])
        : initialSelectedOptionValues.filter(
            (currentValue) => currentValue !== value,
          );
      onChange(newValues);
      return newValues;
    },
    [initialSelectedOptionValues, onChange],
  );

  return (
    <>
      <div
        style={{
          overflow: doneRendering ? "visible" : "hidden",
          height: doneRendering ? "auto" : heightByMaxRows,
        }}
      >
        <OptionListContainer
          aria-label={title}
          role="list"
          currentHeight={
            containerHeight && doneRendering ? currentContainerHeight : "auto"
          }
          ref={(node: HTMLUListElement | null) => {
            if (node && !SSR) {
              setContainerHeight(hideButton ? "auto" : node.scrollHeight);
            }
          }}
        >
          {options.map((option, index) => {
            const checked = initialSelectedOptionValues.includes(option.value);
            return (
              <OptionContainer
                key={`${option.value}${index}`}
                {...(!index
                  ? {
                      ref: (node: HTMLDivElement | null) => {
                        if (!SSR) {
                          setOptionHeight(node?.offsetHeight || defaultHeight);
                        }
                      },
                    }
                  : {})}
              >
                <Option
                  option={option}
                  checked={checked}
                  onChange={() => handleChange(checked, option.value)}
                  variant={variant}
                  seoHrefPrefix={seoHrefPrefix}
                />
              </OptionContainer>
            );
          })}
        </OptionListContainer>
        {showSeeMore && (
          <NoSsr>
            <ButtonContainer>
              <SeeMoreButton
                onClick={() => {
                  setOpen(true);
                  setTimeout(() => setHideButton(true), animationTime);
                }}
              >
                {theme.settings.pagination.seeMoreLabel || SEE_MORE}
              </SeeMoreButton>
            </ButtonContainer>
          </NoSsr>
        )}
      </div>
    </>
  );
};

export default OptionList;
